import {
  COVER_HUES,
  STT_USD_PER_HOUR,
  type AdminEstimate,
  type AdminJob,
  type Lang,
  type UploadMode,
} from '@fonus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  BookMarked,
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AutoTextarea, Field, inputClass, PageTitle } from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import {
  emptyUploadLang,
  isUploadLangReady,
  UploadLang,
  type UploadLangValue,
} from '../components/UploadLang.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { api } from '../lib/api.ts';
import { humanFromApi, humanFromJob } from '../lib/errors.ts';
import { credits, LANG_LABEL, money, num } from '../lib/format.ts';
import { LIVE_MS, useReadiness } from '../lib/queries.ts';
import { createBlocker } from '../lib/readiness.ts';
import { go } from '../lib/router.ts';
import { cn, newKey } from '../lib/utils.ts';

type Source = 'AI' | 'FOLK' | 'AUTHOR' | 'UPLOAD';
type StoryKind = 'AI' | 'FOLK' | 'AUTHOR';
const LANGS: Lang[] = ['uz', 'ru', 'en'];
const empty = { title: '', summary: '', text: '' };
const wordsOf = (t: string) => t.split(/\s+/).filter(Boolean).length;

function StepDots({ step }: { step: 1 | 2 | 3 }) {
  const names = ['Источник', 'Стоимость', 'Создание'];
  return (
    <ol className="mb-5 flex flex-wrap gap-2" aria-label="Шаги">
      {names.map((n, i) => (
        <li
          key={n}
          className={cn(
            'rounded-full px-3 py-1 text-[13px] font-semibold',
            i + 1 === step
              ? 'bg-accent text-white'
              : i + 1 < step
                ? 'bg-ok/12 text-ok'
                : 'bg-fill text-muted',
          )}
          aria-current={i + 1 === step ? 'step' : undefined}
        >
          {i + 1}. {n}
        </li>
      ))}
    </ol>
  );
}

interface Progress {
  job: AdminJob;
  storyJobs: AdminJob[];
  story: {
    id: string;
    status: string;
    reviewFlags: { kind: string; title?: string; storyId?: string }[] | null;
  } | null;
}

type StageState = 'todo' | 'active' | 'done' | 'failed';
function stages(
  p: Progress | undefined,
  source: Source,
  modes?: Record<Lang, UploadMode>,
): { label: string; state: StageState; job?: AdminJob }[] {
  if (!p) return [];
  const gen =
    p.storyJobs.find((j) => j.type === 'GENERATE_STORY') ??
    (p.job.type === 'GENERATE_STORY' ? p.job : undefined);
  const tts = (l: Lang) =>
    [...p.storyJobs]
      .reverse()
      .find((j) => (j.type === 'TTS' || j.type === 'UPLOAD_PROCESS') && j.lang === l);
  const st = (j: AdminJob | undefined): StageState =>
    !j
      ? 'todo'
      : j.status === 'SUCCEEDED'
        ? 'done'
        : j.status === 'FAILED' || j.status === 'CANCELLED'
          ? 'failed'
          : j.status === 'RUNNING'
            ? 'active'
            : 'todo';
  const out: { label: string; state: StageState; job?: AdminJob }[] = [];
  if (source === 'AI' && gen) {
    const stage = (gen.output as { stage?: string } | null)?.stage;
    const genState = st(gen);
    out.push({
      label: 'Текст',
      state:
        genState === 'failed' && stage !== 'review'
          ? 'failed'
          : stage === 'review' || genState === 'done'
            ? 'done'
            : genState,
      job: gen,
    });
    out.push({
      label: 'Рецензия',
      state:
        genState === 'done'
          ? 'done'
          : stage === 'review'
            ? genState === 'failed'
              ? 'failed'
              : 'active'
            : 'todo',
      job: gen,
    });
  }
  for (const l of LANGS) {
    const j = tts(l);
    const what =
      modes?.[l] === 'audio'
        ? 'Распознавание'
        : modes?.[l] === 'audio_text'
          ? 'Синхронизация'
          : 'Озвучка';
    out.push({ label: `${what} ${l}`, state: st(j), ...(j ? { job: j } : {}) });
  }
  const allTts = LANGS.every((l) => st(tts(l)) === 'done');
  out.push({
    label: 'Обработка',
    state: allTts ? 'done' : LANGS.some((l) => st(tts(l)) === 'active') ? 'active' : 'todo',
  });
  out.push({
    label: 'Готово к проверке',
    state: p.story?.status === 'IN_REVIEW' ? 'done' : 'todo',
  });
  return out;
}

function StageIcon({ state }: { state: StageState }) {
  if (state === 'done') return <CheckCircle2 className="size-5 text-ok" />;
  if (state === 'active') return <Loader2 className="size-5 animate-spin text-accent" />;
  if (state === 'failed') return <CircleAlert className="size-5 text-bad" />;
  return <CircleDashed className="size-5 text-muted" />;
}

export function NewStory() {
  const readiness = useReadiness();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [source, setSource] = useState<Source>('AI');
  const [kind, setKind] = useState<StoryKind>('FOLK');
  const [uploads, setUploads] = useState<Record<Lang, UploadLangValue>>({
    uz: emptyUploadLang(),
    ru: emptyUploadLang(),
    en: emptyUploadLang(),
  });
  const textLangs = LANGS.filter((l) => uploads[l].mode === 'text');
  const blocker = createBlocker(readiness.data, source === 'UPLOAD' ? textLangs : undefined);
  const [theme, setTheme] = useState('');
  const [form, setForm] = useState({
    hue: 30,
    ageMin: 2,
    ageMax: 7,
    tags: '',
    versions: { uz: empty, ru: empty, en: empty },
  });
  const [similar, setSimilar] = useState<{ id: string; title: string }[]>([]);
  const [key] = useState(newKey);
  const [jobId, setJobId] = useState<string | null>(null);
  const [storyId, setStoryId] = useState<string | null>(null);

  const manualChars =
    source === 'UPLOAD'
      ? textLangs.reduce((a, l) => a + uploads[l].text.length, 0)
      : LANGS.reduce((a, l) => a + form.versions[l].text.length, 0);
  const sttSec = LANGS.filter((l) => uploads[l].mode === 'audio').reduce(
    (a, l) => a + (uploads[l].file?.durationSec ?? 0),
    0,
  );
  const uploadOk = LANGS.every((l) => isUploadLangReady(uploads[l]));
  const estimate = useQuery({
    queryKey: ['estimate'],
    queryFn: () => api<AdminEstimate>('/admin/estimate'),
    enabled: step === 2,
  });
  const manualOk = LANGS.every(
    (l) =>
      form.versions[l].title.trim().length >= 2 &&
      form.versions[l].summary.trim().length >= 10 &&
      form.versions[l].text.trim().length >= 200,
  );

  const start = useMutation({
    mutationFn: async () => {
      if (source === 'AI') {
        const r = await api<{ jobId: string }>('/admin/generate', {
          method: 'POST',
          body: { idempotencyKey: key, ...(theme.trim() ? { theme: theme.trim() } : {}) },
        });
        return { jobId: r.jobId, storyId: null as string | null };
      }
      const common = {
        idempotencyKey: key,
        hue: form.hue,
        ageMin: form.ageMin,
        ageMax: form.ageMax,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
      if (source === 'UPLOAD') {
        const versions = Object.fromEntries(
          LANGS.map((l) => {
            const v = uploads[l];
            const file = v.file?.key
              ? {
                  uploadKey: v.file.key,
                  fileName: v.file.name,
                  sizeBytes: v.file.size,
                  ...(v.file.durationSec ? { durationSec: v.file.durationSec } : {}),
                }
              : {};
            return [
              l,
              {
                mode: v.mode,
                title: v.title.trim(),
                summary: v.summary.trim(),
                ...(v.mode === 'audio' ? {} : { text: v.text.trim() }),
                ...(v.mode === 'text' ? {} : file),
              },
            ];
          }),
        );
        const r = await api<{ id: string }>('/admin/stories/upload', {
          method: 'POST',
          body: { ...common, sourceType: kind, versions },
        });
        return { jobId: null as string | null, storyId: r.id };
      }
      const r = await api<{ id: string }>('/admin/stories', {
        method: 'POST',
        body: {
          idempotencyKey: key,
          sourceType: source,
          hue: form.hue,
          ageMin: form.ageMin,
          ageMax: form.ageMax,
          tags: form.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          versions: form.versions,
        },
      });
      return { jobId: null as string | null, storyId: r.id };
    },
    onSuccess: (r) => {
      toast.success(
        source === 'AI'
          ? 'Генерация запущена'
          : source === 'UPLOAD'
            ? 'Сказка сохранена, идёт обработка'
            : 'Сказка сохранена, идёт озвучка',
      );
      setJobId(r.jobId);
      setStoryId(r.storyId);
      setStep(3);
      void qc.invalidateQueries({ queryKey: ['readiness'] });
    },
  });

  // Live progress: the generation job (AI) or the story's voicing jobs (manual).
  const progress = useQuery({
    queryKey: ['progress', jobId, storyId],
    queryFn: async (): Promise<Progress> => {
      if (jobId) return api<Progress>(`/admin/jobs/${jobId}`);
      const s = await api<{
        jobs: AdminJob[];
        status: string;
        flags: { kind: string; title?: string; storyId?: string }[];
      }>(`/admin/stories/${storyId ?? ''}`);
      const first = s.jobs[s.jobs.length - 1];
      if (!first) throw new Error('no jobs');
      return {
        job: first,
        storyJobs: [...s.jobs].reverse(),
        story: { id: storyId ?? '', status: s.status, reviewFlags: s.flags },
      };
    },
    enabled: step === 3 && (jobId !== null || storyId !== null),
    refetchInterval: (q) => (q.state.data?.story?.status === 'IN_REVIEW' ? false : LIVE_MS),
  });
  const list = stages(
    progress.data,
    source,
    source === 'UPLOAD'
      ? { uz: uploads.uz.mode, ru: uploads.ru.mode, en: uploads.en.mode }
      : undefined,
  );
  const failed = list.find((s) => s.state === 'failed')?.job;
  const done = progress.data?.story?.status === 'IN_REVIEW';
  const createdStoryId = progress.data?.story?.id ?? storyId;
  const similarAfter = (progress.data?.story?.reviewFlags ?? []).filter(
    (f) => f.kind === 'similar',
  );

  const retry = useMutation({
    mutationFn: (id: string) =>
      api<{ jobId: string }>(`/admin/jobs/${id}/retry`, {
        method: 'POST',
        body: { idempotencyKey: newKey() },
      }),
    onSuccess: () => {
      toast.success('Повтор запущен');
      void progress.refetch();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/admin/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Отменено');
      void progress.refetch();
    },
  });

  const checkSimilar = async (title: string) => {
    if (title.trim().length < 3) return;
    const r = await api<{ items: { id: string; title: string }[] }>(
      `/admin/stories/similar?title=${encodeURIComponent(title.trim())}`,
    ).catch(() => ({ items: [] }));
    setSimilar((prev) => [...prev.filter((p) => !r.items.some((x) => x.id === p.id)), ...r.items]);
  };

  return (
    <div>
      <button
        type="button"
        className="mb-3 inline-flex items-center gap-1 text-[14px] text-accent"
        onClick={() => go('stories')}
      >
        <ArrowLeft className="size-4" /> Сказки
      </button>
      <PageTitle title="Новая сказка" />
      <StepDots step={step} />

      {step === 1 && (
        <Card>
          <CardContent className="flex flex-col gap-5 pt-5">
            <Tabs value={source} onValueChange={(v) => setSource(v as Source)}>
              <TabsList aria-label="Источник">
                <TabsTrigger value="AI">
                  <Sparkles className="mr-1 inline size-4" /> Придумать (AI)
                </TabsTrigger>
                <TabsTrigger value="FOLK">Народная</TabsTrigger>
                <TabsTrigger value="AUTHOR">Авторская</TabsTrigger>
                <TabsTrigger value="UPLOAD">
                  <Upload className="mr-1 inline size-4" /> Загрузить готовое аудио
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {source === 'UPLOAD' && (
              <div className="flex flex-col gap-1.5">
                <p className="text-[13px] font-semibold text-muted">Тип сказки</p>
                <div className="flex gap-2" role="radiogroup" aria-label="Тип сказки">
                  {(
                    [
                      ['FOLK', 'Народная'],
                      ['AUTHOR', 'Авторская'],
                      ['AI', 'AI'],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={kind === id}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-[13px] font-semibold',
                        kind === id
                          ? 'border-accent bg-accent-soft text-accent-strong'
                          : 'border-line text-muted',
                      )}
                      onClick={() => setKind(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[12px] text-muted">
                  Аудио не переозвучивается. На карточке будет пометка «аудио загружено вручную».
                </p>
              </div>
            )}
            {source === 'AI' ? (
              <Field
                label="Тема (необязательно)"
                hint="Claude напишет сказку на трёх языках, затем отрецензирует её. Названия существующих сказок он не повторит."
              >
                <input
                  className={inputClass}
                  value={theme}
                  maxLength={300}
                  placeholder="например: аист возвращается в махаллю весной"
                  onChange={(e) => setTheme(e.target.value)}
                />
              </Field>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-6">
                  <Field label="Цвет обложки">
                    <div className="flex gap-2" role="radiogroup" aria-label="Цвет обложки">
                      {COVER_HUES.map((h) => (
                        <button
                          key={h}
                          type="button"
                          role="radio"
                          aria-checked={form.hue === h}
                          aria-label={`Оттенок ${String(h)}`}
                          className={cn(
                            'size-9 rounded-full ring-offset-2 ring-offset-card transition',
                            form.hue === h && 'ring-2 ring-accent',
                          )}
                          style={{
                            background: `linear-gradient(140deg, hsl(${String(h)} 80% 78%), hsl(${String((h + 40) % 360)} 70% 62%))`,
                          }}
                          onClick={() => setForm({ ...form, hue: h })}
                        />
                      ))}
                    </div>
                  </Field>
                  <Field label="Возраст">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={2}
                        max={7}
                        className={cn(inputClass, 'w-20')}
                        value={form.ageMin}
                        onChange={(e) => setForm({ ...form, ageMin: Number(e.target.value) })}
                        aria-label="Возраст от"
                      />
                      <span className="text-muted">–</span>
                      <input
                        type="number"
                        min={2}
                        max={7}
                        className={cn(inputClass, 'w-20')}
                        value={form.ageMax}
                        onChange={(e) => setForm({ ...form, ageMax: Number(e.target.value) })}
                        aria-label="Возраст до"
                      />
                      <span className="text-[14px] text-muted">лет</span>
                    </div>
                  </Field>
                  <Field label="Теги через запятую" className="min-w-[200px] flex-1">
                    <input
                      className={inputClass}
                      value={form.tags}
                      onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    />
                  </Field>
                </div>
                {source === 'UPLOAD' &&
                  LANGS.map((l) => (
                    <UploadLang
                      key={l}
                      lang={l}
                      value={uploads[l]}
                      onChange={(update) => setUploads((u) => ({ ...u, [l]: update(u[l]) }))}
                      onTitleBlur={(t) => void checkSimilar(t)}
                    />
                  ))}
                {source !== 'UPLOAD' &&
                  LANGS.map((l) => {
                    const v = form.versions[l];
                    const w = wordsOf(v.text);
                    const set = (patch: Partial<typeof v>) =>
                      setForm({ ...form, versions: { ...form.versions, [l]: { ...v, ...patch } } });
                    return (
                      <fieldset
                        key={l}
                        className="flex flex-col gap-3 rounded-[16px] border border-line p-4"
                      >
                        <legend className="px-1 text-[14px] font-semibold">{LANG_LABEL[l]}</legend>
                        <input
                          className={inputClass}
                          placeholder="Название"
                          value={v.title}
                          onChange={(e) => set({ title: e.target.value })}
                          onBlur={(e) => void checkSimilar(e.target.value)}
                        />
                        <AutoTextarea
                          placeholder="Кратко для родителей"
                          value={v.summary}
                          onChange={(e) => set({ summary: e.target.value })}
                        />
                        <AutoTextarea
                          placeholder="Текст (абзацы — через пустую строку)"
                          value={v.text}
                          onChange={(e) => set({ text: e.target.value })}
                        />
                        <span
                          className={cn(
                            'text-[13px]',
                            w < 550 ? 'text-bad' : w > 700 ? 'text-warn' : 'text-ok',
                          )}
                        >
                          {w} слов (рекомендуется 550–700)
                        </span>
                      </fieldset>
                    );
                  })}
                {similar.length > 0 && (
                  <div className="rounded-[14px] border border-warn/30 bg-warn/[.07] p-3 text-[14px]">
                    Похожие названия уже есть:{' '}
                    {similar.map((x, i) => (
                      <span key={x.id}>
                        {i > 0 && ', '}
                        <a className="text-accent" href={`#/stories/${x.id}`}>
                          «{x.title}»
                        </a>
                      </span>
                    ))}
                    . Проверьте, что это не дубль.
                  </div>
                )}
              </>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={
                  blocker !== null ||
                  (source === 'UPLOAD' ? !uploadOk : source !== 'AI' && !manualOk)
                }
                onClick={() => setStep(2)}
              >
                Далее <ArrowRight />
              </Button>
              {blocker ? (
                <span className="text-[13px] text-muted">
                  {blocker} —{' '}
                  <a className="text-accent" href="#/">
                    открыть «Готовность»
                  </a>
                </span>
              ) : source === 'UPLOAD' ? (
                !uploadOk && (
                  <span className="text-[13px] text-muted">
                    Для каждого языка: название, описание и файл или текст (от 200 символов)
                  </span>
                )
              ) : (
                source !== 'AI' &&
                !manualOk && (
                  <span className="text-[13px] text-muted">
                    Заполните название, описание и текст на всех трёх языках
                  </span>
                )
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Подтвердите стоимость</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {estimate.isLoading && <p className="text-muted">Считаем…</p>}
            {estimate.data &&
              (() => {
                const e = estimate.data;
                const need = source === 'AI' ? e.credits : manualChars;
                const left = e.creditsRemaining === null ? null : e.creditsRemaining - need;
                const monthLeft = e.monthLeft - need;
                const enough = monthLeft >= 0 && (left === null || left >= 0);
                const ttsMoney = {
                  usd: (e.money.tts.usd / Math.max(1, e.credits)) * need,
                  uzs:
                    e.money.tts.uzs === null
                      ? null
                      : Math.round((e.money.tts.uzs / Math.max(1, e.credits)) * need),
                };
                return (
                  <>
                    <p className="text-[17px] leading-relaxed">
                      ≈<b>{num(need)}</b> кредитов ElevenLabs ({money(ttsMoney)})
                      {source === 'AI' && (
                        <>
                          {' '}
                          + ≈<b>{money(e.money.claude)}</b> Claude
                        </>
                      )}
                      .
                      {left !== null && (
                        <>
                          {' '}
                          На счёте останется <b>{credits(left)}</b>.
                        </>
                      )}{' '}
                      В месячном лимите останется {credits(monthLeft)}.
                    </p>
                    {source === 'UPLOAD' && (
                      <ul className="flex flex-col gap-1 text-[14px] text-muted">
                        {sttSec > 0 && (
                          <li>
                            Распознавание речи ({Math.round(sttSec / 60)} мин): ≈
                            {(() => {
                              const usdSum = (sttSec / 3600) * STT_USD_PER_HOUR;
                              return money({
                                usd: usdSum,
                                uzs: e.fx ? Math.round(usdSum * e.fx.rate) : null,
                              });
                            })()}{' '}
                            (Scribe v2, ${STT_USD_PER_HOUR.toFixed(2).replace('.', ',')} за час).
                          </li>
                        )}
                        {LANGS.some((l) => uploads[l].mode === 'audio_text') && (
                          <li>
                            Сверка текста с записью: ElevenLabs не публикует цену — фактический
                            расход будет в журнале.
                          </li>
                        )}
                        <li>+ рецензия текста Claude, когда будут готовы все три текста.</li>
                      </ul>
                    )}
                    {e.fx && (
                      <p className="text-[13px] text-muted">
                        Курс ЦБ: 1 $ = {num(e.fx.rate)} сум
                        {e.fx.source === 'cbu' ? ` на ${e.fx.date}` : ' (задан вручную)'}.
                      </p>
                    )}
                    {!enough && (
                      <ErrorCard
                        error={{
                          title: 'Не хватает кредитов',
                          why:
                            monthLeft < 0
                              ? 'Не укладываемся в месячный лимит.'
                              : 'На счёте ElevenLabs не хватит кредитов.',
                          todo:
                            monthLeft < 0
                              ? 'Увеличьте лимит в настройках.'
                              : 'Пополните тариф ElevenLabs.',
                          action: { label: 'Настройки', href: '#/settings' },
                          technical: { code: null, requestId: null, text: null },
                        }}
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button variant="secondary" onClick={() => setStep(1)}>
                        <ArrowLeft /> Назад
                      </Button>
                      <Button
                        disabled={!enough || start.isPending || blocker !== null}
                        onClick={() => start.mutate()}
                      >
                        {start.isPending ? <Loader2 className="animate-spin" /> : <BookMarked />}{' '}
                        Продолжить
                      </Button>
                    </div>
                    {start.error && <ErrorCard error={humanFromApi(start.error)} />}
                  </>
                );
              })()}
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>{done ? 'Готово к проверке' : 'Создаём сказку…'}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <ol className="flex flex-col gap-2" aria-live="polite">
              {list.map((s) => (
                <li key={s.label} className="flex items-center gap-3 text-[15px]">
                  <StageIcon state={s.state} />
                  <span className={s.state === 'todo' ? 'text-muted' : ''}>{s.label}</span>
                  {s.job && s.job.retryLog.length > 0 && s.state !== 'done' && (
                    <Badge tone="warn">повтор {s.job.retryLog.length} из 2</Badge>
                  )}
                </li>
              ))}
            </ol>
            {failed && (
              <ErrorCard
                error={humanFromJob(failed)}
                extra={
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(failed.id)}
                  >
                    Повторить
                  </Button>
                }
              />
            )}
            {!done && !failed && progress.data && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const active = [progress.data.job, ...progress.data.storyJobs].find(
                      (j) => j.status === 'QUEUED' || j.status === 'RUNNING',
                    );
                    if (active) cancel.mutate(active.id);
                  }}
                >
                  <X /> Отменить
                </Button>
              </div>
            )}
            {done && similarAfter.length > 0 && (
              <div className="rounded-[14px] border border-warn/30 bg-warn/[.07] p-3 text-[14px]">
                Похожее название уже есть:{' '}
                {similarAfter.map((f) => `«${f.title ?? ''}»`).join(', ')}. Проверьте, что это не
                дубль.
              </div>
            )}
            {done && createdStoryId && (
              <Button size="lg" onClick={() => go(`stories/${createdStoryId}`)}>
                Открыть на проверку
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
